/**
 * Products API Client
 *
 * Wraps all endpoints from the tms-api-products service.
 *
 * Follows the same gateway + service-prefix pattern as tms-app-soho:
 *   - Uses getApiClient() (shared gateway client) from @/core/services/api.service
 *   - Routes are prefixed with API_ROUTES.PRODUCTS (= /products/api/v1)
 *   - createApiClient from @tms-phoenix/ui-vue auto-unwraps the ApiResponse envelope
 *   - A response interceptor (LIFO, runs before the unwrapper) captures the flat
 *     products API pagination meta and attaches it to response.pagination
 *   - All function return types therefore use the INNER data type (not the envelope)
 *
 * Callers read:
 *   response.data                                → items array / single object
 *   (response as any).pagination                 → { total, page, limit, totalPages }
 */
import type { AxiosResponse } from 'axios';
import { getApiClient } from '@/core/services/api.service';
import { API_ROUTES } from '@/core/config';
import type {
  ApiProductListItem,
  ApiProductDetail,
  ApiLookupItem,
  ApiLookupType,
  CreateProductApiRequest,
  UpdateProductApiRequest,
  ProductListQuery,
  ImageUploadData,
} from '../types/product-api.types';
import type { CsvUploadResult } from '@tms-phoenix/tms-types';

/** Service prefix for the products micro-service via the gateway. */
const API_PREFIX = API_ROUTES.PRODUCTS;
const api = getApiClient();

// ---------------------------------------------------------------------------
// Products list
// ---------------------------------------------------------------------------

/**
 * GET /api/v1/products
 *
 * Paginated list of products with optional search, filter, and sort.
 * Allergens are NOT included in list results.
 *
 * response.data       = ApiProductListItem[]  (unwrapped by createApiClient)
 * response.pagination = { total, page, limit, totalPages }  (captured before unwrap)
 */
export function fetchProducts(
  query: ProductListQuery = {},
): Promise<AxiosResponse<ApiProductListItem[]>> {
  return api.get<ApiProductListItem[]>(
    `${API_PREFIX}/products`,
    { params: query },
  );
}

// ---------------------------------------------------------------------------
// Single product
// ---------------------------------------------------------------------------

/**
 * GET /api/v1/products/:id
 *
 * Full product detail including attributes (~130 nutrient fields) and allergens[].
 * id must be a valid UUID.
 *
 * response.data = ApiProductDetail  (unwrapped)
 */
export function fetchProductById(
  id: string,
): Promise<AxiosResponse<ApiProductDetail>> {
  return api.get<ApiProductDetail>(
    `${API_PREFIX}/products/${id}`,
  );
}

// ---------------------------------------------------------------------------
// Create product
// ---------------------------------------------------------------------------

/**
 * POST /api/v1/products
 *
 * Creates a new product.  Returns 201 Created with the full ProductDetail.
 * Returns 409 Conflict if the SKU already exists.
 *
 * response.data = ApiProductDetail  (unwrapped)
 */
export function createProduct(
  body: CreateProductApiRequest,
): Promise<AxiosResponse<ApiProductDetail>> {
  return api.post<ApiProductDetail>(
    `${API_PREFIX}/products`,
    body,
  );
}

// ---------------------------------------------------------------------------
// Update product  (PATCH — partial)
// ---------------------------------------------------------------------------

/**
 * PATCH /api/v1/products/:id
 *
 * Partially updates an existing product. Only provided fields are written.
 * Passing allergens[] REPLACES all existing allergen rows. Omit to leave unchanged.
 * Returns 409 on SKU conflict with a different product.
 *
 * response.data = ApiProductDetail  (unwrapped, updated record)
 */
export function updateProduct(
  id: string,
  body: UpdateProductApiRequest,
): Promise<AxiosResponse<ApiProductDetail>> {
  return api.patch<ApiProductDetail>(
    `${API_PREFIX}/products/${id}`,
    body,
  );
}

// ---------------------------------------------------------------------------
// Product image upload
// ---------------------------------------------------------------------------

/**
 * PUT /api/v1/products/:id/image
 *
 * Uploads a product image as multipart/form-data.
 * Accepts JPEG / PNG / WebP / GIF; max 5 MB per file.
 * Returns the image_url stored on the product record (may be a relative path
 * such as /uploads/products/abc.jpg or a fully-qualified URL depending on the
 * storage backend). Pass the returned value directly to getProductImageProxyUrl(),
 * which normalizes relative paths to absolute URLs before forwarding to the proxy.
 *
 * No explicit Content-Type header is needed — Axios detects the FormData body
 * and sets `multipart/form-data` with the correct boundary automatically.
 *
 * response.data = ImageUploadData  ({ product_id, image_url })
 */
export function uploadProductImage(
  id: string,
  file: File,
): Promise<AxiosResponse<ImageUploadData>> {
  const form = new FormData();
  form.append('image', file);
  return api.put<ImageUploadData>(
    `${API_PREFIX}/products/${id}/image`,
    form,
  );
}

// ---------------------------------------------------------------------------
// Product image proxy
// ---------------------------------------------------------------------------

/**
 * GET /api/v1/products/image?url=...
 *
 * Proxy-fetches an image by URL and returns its binary content.
 * Pass the raw (unencoded) image URL; it will be URL-encoded as a `url`
 * query parameter by the HTTP client.
 * Useful for displaying product images stored as external URLs
 * without running into CORS issues.
 *
 * Returns AxiosResponse with binary data (responseType: 'blob').
 */
export function fetchProductImageProxy(
  imageUrl: string,
): Promise<AxiosResponse<Blob>> {
  return api.get<Blob>(
    `${API_PREFIX}/products/image`,
    {
      params: { url: imageUrl },
      responseType: 'blob',
    },
  );
}

/**
 * Build a URL string pointing to the image proxy endpoint.
 * Can be used directly as an <img src> value.
 *
 * Accepts both fully-qualified URLs (https://cdn.example.com/img.jpg) and
 * relative paths (/uploads/products/abc.jpg) returned by uploadProductImage().
 * Relative paths are resolved against the API base URL so the proxy endpoint
 * always receives an absolute URL it can fetch.
 *
 * @param imageUrl - Fully-qualified or relative URL of the image to proxy.
 * @returns A URL string like `http://localhost:5004/api/v1/products/image?url=...`
 */
export function getProductImageProxyUrl(imageUrl: string): string {
  const base = (api.defaults.baseURL || '').replace(/\/+$/, '');
  // Normalize relative paths (e.g. /uploads/...) to absolute URLs so the backend
  // proxy can fetch them. Already-absolute URLs (http:// / https://) are unchanged.
  const absoluteUrl =
    imageUrl.startsWith('http://') || imageUrl.startsWith('https://')
      ? imageUrl
      : `${base}${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
  return `${base}${API_PREFIX}/products/image?url=${encodeURIComponent(absoluteUrl)}`;
}

// ---------------------------------------------------------------------------
// Lookups  (manufacturer | brand | category | form)
// ---------------------------------------------------------------------------

/**
 * GET /api/v1/lookups?type=...&manufacturer_name=...
 *
 * Lists all items for the requested lookup table.
 * type: 'manufacturer' | 'brand' | 'category' | 'form'
 * When type=brand, pass manufacturer_name to filter brands by manufacturer.
 *
 * response.data = ApiLookupItem[]  (unwrapped)
 */
export function fetchLookups(
  type: ApiLookupType,
  manufacturer_name?: string,
): Promise<AxiosResponse<ApiLookupItem[]>> {
  return api.get<ApiLookupItem[]>(
    `${API_PREFIX}/lookups`,
    { params: { type, ...(manufacturer_name ? { manufacturer_name } : {}) } },
  );
}

// ---------------------------------------------------------------------------
// CSV bulk upload
// ---------------------------------------------------------------------------

/**
 * POST /api/v1/products/csv/upload
 *
 * Uploads a CSV file to add or update products in bulk.
 * Sent as multipart/form-data — field name "file".
 * No explicit Content-Type header needed; Axios sets it with the boundary
 * automatically when it detects a FormData body.
 *
 * response.data = CsvUploadResult  (unwrapped by createApiClient)
 */
export function uploadProductsCsv(
  file: File,
): Promise<AxiosResponse<CsvUploadResult>> {
  const form = new FormData();
  form.append('file', file);
  return api.post<CsvUploadResult>(
    `${API_PREFIX}/products/csv/upload`,
    form,
  );
}


